import type { StateCreator } from 'zustand'
import type { DashboardStore, PersistMutator } from './index'
import { DEFAULT_NOTIFICATION_SOUND_ID, type NotificationSoundId } from '@/lib/notification-sound'

/** Whether the tasks views show only the user's tasks or every task. */
export type TaskScope = 'mine' | 'all'

export interface UiSlice {
  /**
   * Persisted "My tasks only" toggle. Defaults to 'all' so the first view is
   * the whole-business overview; thereafter it sticks to the user's last
   * choice across navigation and reloads.
   */
  taskScope: TaskScope
  setTaskScope: (scope: TaskScope) => void
  /**
   * Contact id of the chat conversation currently open on screen, or null when
   * none is. Lets realtime code stay quiet about a message for the thread the
   * user is already reading. Ephemeral (not persisted) — it's whichever
   * `<ChatContent>` is mounted, independent of the URL.
   */
  openChatContactId: string | null
  setOpenChatContactId: (contactId: string | null) => void
  /**
   * Whether to play a chime for foreground notifications. This is a per-device
   * preference (the sound is browser Web Audio), so it lives with the other
   * persisted local UI state rather than syncing through the API.
   */
  notificationSoundEnabled: boolean
  setNotificationSoundEnabled: (enabled: boolean) => void
  /** Which chime preset to play (see `NOTIFICATION_SOUNDS`). */
  notificationSoundId: NotificationSoundId
  setNotificationSoundId: (soundId: NotificationSoundId) => void
}

export const createUiSlice: StateCreator<DashboardStore, PersistMutator, [], UiSlice> = (set) => ({
  taskScope: 'all',
  setTaskScope: (scope) => set({ taskScope: scope }),
  openChatContactId: null,
  setOpenChatContactId: (contactId) => set({ openChatContactId: contactId }),
  notificationSoundEnabled: true,
  setNotificationSoundEnabled: (enabled) => set({ notificationSoundEnabled: enabled }),
  notificationSoundId: DEFAULT_NOTIFICATION_SOUND_ID,
  setNotificationSoundId: (soundId) => set({ notificationSoundId: soundId }),
})
